<!--
  Functional delete-account flow (login-gated). Mirrors
  crop-swipe/lib/features/profile/delete_account_screen.dart.
  See delete-account.component.ts for the state-machine doc.
-->
<div class="delete-account-page">
  <div class="container py-4">
    <div class="row justify-content-center">
      <div class="col-lg-8">

        <!-- Page header -->
        <header class="page-header mb-4">
          <h1 class="page-title">
            <i class="bi bi-shield-exclamation me-2"></i>
            Delete Account
          </h1>
        </header>

        <!-- ============================== Loading ============================== -->
        <ng-container *ngIf="viewState === 'loading'">
          <div class="text-center py-5">
            <div class="spinner-border text-danger" role="status">
              <span class="visually-hidden">Loading deletion preview...</span>
            </div>
            <p class="text-muted mt-3">Checking what would be deleted…</p>
          </div>
        </ng-container>

        <!-- ============================== Preview error ============================== -->
        <ng-container *ngIf="viewState === 'preview-error'">
          <div class="alert alert-danger" role="alert">
            <i class="bi bi-exclamation-triangle me-2"></i>
            <strong>Couldn't load the deletion preview.</strong>
            <p class="mb-2 mt-2">{{ previewError }}</p>
            <button type="button" class="btn btn-outline-danger btn-sm me-2"
                    (click)="loadPreview()">
              Try again
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm"
                    (click)="goBack()">
              Go back
            </button>
          </div>
        </ng-container>

        <!-- ============================== Blocked ============================== -->
        <ng-container *ngIf="viewState === 'blocked' && preview as p">
          <div class="card destructive-card mb-4">
            <div class="card-body">
              <div class="state-header mb-3">
                <i class="bi bi-clock-history text-danger fs-1"></i>
                <h2 class="h4 mb-1 mt-2">Resolve these first</h2>
                <p class="text-secondary mb-0">
                  Your account can't be deleted while you have active commitments.
                  Wrap these up, then come back.
                </p>
              </div>
              <ul class="list-unstyled blocker-list mb-0">
                <li *ngFor="let b of p.blockers" class="py-2 d-flex align-items-center">
                  <i class="bi bi-dot text-secondary me-1"></i>
                  <span>{{ b.count }} {{ blockerLabel(b) }}</span>
                </li>
              </ul>
            </div>
          </div>
          <button type="button" class="btn btn-outline-secondary w-100"
                  (click)="goBack()">
            Go back
          </button>
        </ng-container>

        <!-- ============================== Reassign required ============================== -->
        <ng-container *ngIf="viewState === 'reassign' && preview as p">
          <div class="card mb-4">
            <div class="card-body">
              <div class="state-header mb-3">
                <i class="bi bi-people-fill text-primary fs-1"></i>
                <h2 class="h4 mb-1 mt-2">Assign a new owner first</h2>
                <p class="text-secondary mb-0">
                  You own organizations that have other members. Transfer
                  ownership to someone else in each before deleting your account.
                </p>
              </div>
              <ul class="list-unstyled org-list mb-0">
                <li *ngFor="let o of p.orgsNeedingReassignment" class="py-2 d-flex align-items-center">
                  <i class="bi bi-building text-secondary me-2"></i>
                  <span>{{ o.name }}</span>
                </li>
              </ul>
            </div>
          </div>
          <button type="button" class="btn btn-outline-secondary w-100"
                  (click)="goBack()">
            Go back
          </button>
        </ng-container>

        <!-- ============================== Confirm ============================== -->
        <ng-container *ngIf="viewState === 'confirm' && preview as p">
          <div class="card destructive-card mb-4">
            <div class="card-body">
              <div class="state-header mb-3">
                <i class="bi bi-exclamation-triangle-fill text-danger fs-1"></i>
                <h2 class="h4 mb-1 mt-2">Delete your account?</h2>
                <p class="text-secondary mb-0">This removes your login and access.</p>
              </div>

              <!-- Compliance disclosure (required by both stores) -->
              <div class="disclosure-block mb-3">
                <h3 class="h6 fw-bold mb-2">What happens</h3>
                <p class="text-secondary mb-2">
                  American Drone Network is an FAA compliance platform, so your
                  flight records, spray records, reports, and the identity needed
                  to attribute them are retained as required by law. Your login
                  and access are removed.
                </p>
                <p class="text-secondary fw-semibold mb-0">
                  You'll get a one-time recovery code on the next screen. Keep it
                  safe — it is the only way to regain access later.
                </p>
              </div>

              <!-- Orgs that will be closed (sole-member) -->
              <div *ngIf="p.orgsToBeClosed.length > 0" class="orgs-block mb-3">
                <h3 class="h6 fw-bold mb-1">These organizations will be closed</h3>
                <p class="text-muted small mb-2">You're the only member of each.</p>
                <ul class="list-unstyled mb-0">
                  <li *ngFor="let o of p.orgsToBeClosed" class="py-1 d-flex align-items-center">
                    <i class="bi bi-building text-secondary me-2"></i>
                    <span>{{ o.name }}</span>
                  </li>
                </ul>
              </div>

              <!-- Password re-auth -->
              <div class="reauth-block">
                <label for="confirmPassword" class="form-label fw-bold">Confirm your password</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-lock"></i></span>
                  <input
                    type="password"
                    id="confirmPassword"
                    class="form-control"
                    [(ngModel)]="password"
                    [disabled]="deleting"
                    autocomplete="current-password"
                    placeholder="Your current password"
                  />
                </div>
                <div *ngIf="deleteError" class="text-danger small mt-2">
                  <i class="bi bi-exclamation-circle me-1"></i>{{ deleteError }}
                </div>
              </div>
            </div>
          </div>

          <button type="button" class="btn btn-danger w-100 mb-2"
                  [disabled]="deleting || !password"
                  (click)="submitDelete()">
            <span *ngIf="deleting" class="spinner-border spinner-border-sm me-2"></span>
            Delete my account
          </button>
          <button type="button" class="btn btn-outline-secondary w-100"
                  [disabled]="deleting"
                  (click)="goBack()">
            Cancel
          </button>
        </ng-container>

        <!-- ============================== Success (recovery code) ============================== -->
        <ng-container *ngIf="viewState === 'success'">
          <div class="card success-card mb-4">
            <div class="card-body">
              <div class="state-header mb-3">
                <i class="bi bi-check-circle-fill text-success fs-1"></i>
                <h2 class="h4 mb-1 mt-2">Account deleted</h2>
                <p class="text-secondary mb-0">
                  Save this recovery code. It is the ONLY way to regain access to
                  your data later, and we cannot recover it for you if you lose it.
                </p>
              </div>

              <label class="form-label fw-bold">Recovery code</label>
              <div class="recovery-code-display mb-2">
                <code class="user-select-all">{{ recoveryCode }}</code>
                <button type="button" class="btn btn-sm btn-outline-primary ms-2"
                        (click)="copyRecoveryCode()">
                  <i class="bi bi-clipboard me-1"></i>Copy
                </button>
              </div>
              <p class="text-muted small mb-0">
                Contact <a href="mailto:support@americandronenetwork.com">support&#64;americandronenetwork.com</a>
                with this code if you ever need to regain access.
              </p>
            </div>
          </div>

          <button type="button" class="btn btn-primary w-100"
                  (click)="signOutAndExit()">
            Done — sign out
          </button>
        </ng-container>

      </div>
    </div>
  </div>
</div>
